import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get token from sessionStorage
  const getToken = () => {
    return sessionStorage.getItem('Market-token');
  };

  // Set token in sessionStorage
  const setToken = (token) => {
    sessionStorage.setItem('Market-token', token);
  };

  // Remove token from sessionStorage
  const removeToken = () => {
    sessionStorage.removeItem('Market-token');
  };

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get('https://apisellhub.com/api/super-admin/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status) {
        setUser(response.data.data);
        // Also save to sessionStorage for backward compatibility
        sessionStorage.setItem('user', JSON.stringify(response.data.data));
      } else {
        setError('Failed to fetch user profile');
      }
    } catch (err) {
      console.error('Profile API error:', err);
      if (err.response?.status === 401) {
        // Token expired or invalid
        removeToken();
        sessionStorage.removeItem('user');
        setUser(null);
      } else {
        setError('Failed to fetch user profile');
      }
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      const response = await axios.post('https://apisellhub.com/api/super-admin/login', {
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status && response.data.data?.token) {
        const token = response.data.data.token.admin_token;
        setToken(token);
        
        // Fetch user profile after successful login
        await fetchUserProfile();
        
        return { success: true };
      } else {
        return { success: false, error: 'Login failed' };
      }
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: err.response?.data?.errors[0] || 'Login failed' };
    }
  };

  // Logout function
  const logout = () => {
    removeToken();
    sessionStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!getToken() && !!user;
  };

  // Initialize auth state on app load
  useEffect(() => {
  const fetchProfile = async () => {
      try {
        const token = getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get('https://apisellhub.com/api/super-admin/me', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.status) {
          setUser(response.data.data);
          // Also save to sessionStorage for backward compatibility
          sessionStorage.setItem('user', JSON.stringify(response.data.data));
        } else {
          setError('Failed to fetch user profile');
        }
      } catch (err) {
        console.error('Profile API error:', err);
        if (err.response?.status === 401) {
          // Token expired or invalid
          removeToken();
          sessionStorage.removeItem('user');
          setUser(null);
        } else {
          setError('Failed to fetch user profile');
        }
      } finally {
        setLoading(false);
      }
  };
  fetchProfile()
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    fetchUserProfile,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
